import { useTranslation } from 'react-i18next'

import { useRoleCategoriesContext } from '../../../roleCategoriesContext/RoleCategoriesContext'
import { useDeterministicDetailsMatchingContext } from '../../../deterministicDetailsMatchingContext/DeterministicDetailsMatchingContext'

import { LLMReasoningProps } from './LLMReasoning.types'
import { LLMReasoningRow } from './LLMReasoningRow/LLMReasoningRow'
import { RoleCategoryBadges } from './RoleCategoryBadges/RoleCategoryBadges'
import { CategoryOutcomeBadge } from './CategoryOutcomeBadge/CategoryOutcomeBadge'
import { DocumentValidityBadge } from './DocumentValidityBadge/DocumentValidityBadge'

export function LLMReasoning({ document }: Readonly<LLMReasoningProps>) {
  const { t } = useTranslation()
  const roleCategoriesConfig = useRoleCategoriesContext()
  const isDeterministicDetailsMatching = useDeterministicDetailsMatchingContext()

  const agentName = document?.personalNames?.join(', ')
  const orgName = document?.organizationNames?.join(', ')
  const roles = document?.roles?.join(', ')
  const documentType = document?.documentType
  const issueDate = document?.issueDate
  const documentValidity = document?.documentValidity
  const matchingResults = document?.documentMatchingResults
  const roleCategories = matchingResults?.roleCategories
  const showRoleCategories = !!roleCategoriesConfig && !!roleCategories?.length

  const { nameMatchCategory, orgMatchCategory, documentTypeCategory } = matchingResults ?? {}

  const categoryOutcomeRows = [
    {
      key: 'name-match-category',
      label: t('typography.nameMatchCategoryLabel'),
      category:
        nameMatchCategory &&
        t(`typography.nameMatchCategory.${nameMatchCategory}`, { defaultValue: nameMatchCategory }),
      outcome: matchingResults?.nameMatchOutcome
    },
    {
      key: 'org-match-category',
      label: t('typography.orgMatchCategoryLabel'),
      category:
        orgMatchCategory && t(`typography.orgMatchCategory.${orgMatchCategory}`, { defaultValue: orgMatchCategory }),
      outcome: matchingResults?.orgMatchOutcome
    },
    {
      key: 'document-type-category',
      label: t('typography.documentTypeCategoryLabel'),
      category:
        documentTypeCategory &&
        t(`typography.documentTypeCategory.${documentTypeCategory}`, { defaultValue: documentTypeCategory }),
      outcome: matchingResults?.documentTypeOutcome
    }
  ].filter((row): row is typeof row & { category: string } => isDeterministicDetailsMatching && !!row.category)

  const hasLLMReasoning =
    documentType ||
    agentName ||
    orgName ||
    roles ||
    issueDate ||
    documentValidity ||
    showRoleCategories ||
    categoryOutcomeRows.length > 0

  const hasDeterministicModeAndData =
    isDeterministicDetailsMatching &&
    nameMatchCategory &&
    orgMatchCategory &&
    documentTypeCategory &&
    showRoleCategories

  if (!hasLLMReasoning) {
    return null
  }

  return (
    <div style={{ width: '100%', marginBottom: '16px' }}>
      {agentName && (
        <LLMReasoningRow
          label={t('typography.agentName')}
          value={agentName}
          status={!hasDeterministicModeAndData ? document?.documentMatchingResults?.nameMatchStatus : undefined}
        />
      )}
      {orgName && (
        <LLMReasoningRow
          label={t('typography.orgName')}
          value={orgName}
          status={!hasDeterministicModeAndData ? document?.documentMatchingResults?.organizationMatchStatus : undefined}
        />
      )}
      {roles && (
        <LLMReasoningRow
          label={t('typography.role')}
          value={roles}
          status={!hasDeterministicModeAndData ? document?.documentMatchingResults?.roleValidity : undefined}
        />
      )}
      {documentType && <LLMReasoningRow label={t('typography.documentType')} value={documentType} />}
      {issueDate && <LLMReasoningRow label={t('typography.issueDate')} value={issueDate} />}
      {hasDeterministicModeAndData && documentValidity && (
        <LLMReasoningRow
          label={t('typography.documentValidity')}
          value={<DocumentValidityBadge documentValidity={documentValidity} />}
        />
      )}
      {!hasDeterministicModeAndData && documentValidity && (
        <LLMReasoningRow label={t('typography.documentValidity')} documentValidity={documentValidity} />
      )}
      {categoryOutcomeRows.map(({ key, label, category, outcome }) => (
        <LLMReasoningRow
          key={key}
          label={label}
          value={<CategoryOutcomeBadge label={category} outcome={outcome} testId={key} />}
        />
      ))}
      {showRoleCategories && (
        <LLMReasoningRow
          label={t('typography.roleCategoryLabel')}
          value={
            <RoleCategoryBadges
              categories={roleCategories}
              roleCategoriesConfig={roleCategoriesConfig}
              verdicts={document?.documentMatchingResults?.roleCategoryVerdicts}
            />
          }
        />
      )}
    </div>
  )
}
